<template>
  <span class="ml-2">
    <v-btn
        v-if="canCatch"
        :loading="catchState === 'pending'"
        :disabled="ballCount <= 0"
        color="accent black--text btn-shadow"
        @click="catchPokemon"
    >
      <span :class="{ 'catch-btn-sheen': !alreadyCaught }">{{ $t('journey.catch.catchButtonLabel') }} {{ ballCount }} <v-icon size="25">{{ ballIcon }}</v-icon></span>
    </v-btn>

    <ResponsiveDialog
        v-model="catchDialog"
        hide-default-close
        :header-title="dialogHeaderTitle"
        header-class="skin-window-header--catch"
        card-class="skin-card"
    >
      <div class="catch-dialog-body skin-window--catch">
        <div class="catch-dialog-scroll">
          <template v-if="catchState === 'pending'">
            <v-img
                class="self-center catching"
                :src="require(`@/assets/pic/pokemons${fight.opponentPokemon.pokemonType.img}`)"
                contain
                max-height="260"
            />
            <v-img
                v-if="leaving === false"
                src='@/assets/pic/web/pokeball_catching.gif'
                max-width="100"
                max-height="100"
                class="mx-auto catch-dialog-ball"
            />
            <div class="headline text-center mt-2">{{ $t('journey.catch.waitingText') }}</div>
          </template>

          <template v-else-if="catchState === 'success'">
            <div class="catch-dialog-subtitle">{{ $t('journey.catch.catchSuccessPrefix') }}{{ catchData.pokemon.pokemonType.name }}{{ $t('journey.catch.catchSuccessSuffix') }}</div>
            <PokemonDetail
                :pokemon="catchData.pokemon"
                :min-height="400"
                class="fill-height"
            />
            <v-card-text v-if="catchData.pokemon.inProfesorOak">
              {{ $t('journey.catch.slotsFullPrefix') }}{{ catchData.pokemon.pokemonType.name }}{{ $t('journey.catch.slotsFullSuffix') }}
              <v-img
                  class="float-right"
                  src='@/assets/pic/web/oak_avatar.png'
                  max-width="100"
                  max-height="100"
              />
            </v-card-text>
          </template>

          <template v-else-if="catchState === 'lose'">
            <v-img
                v-if="fight.opponentPokemon"
                class="self-center catching"
                :src="require(`@/assets/pic/pokemons${fight.opponentPokemon.pokemonType.img}`)"
                contain
                max-height="260"
            />
            <v-img
                src='@/assets/pic/web/pokeball_leave.png'
                max-width="130"
                max-height="130"
                class="mx-auto catch-dialog-ball"
            />
            <div class="catch-dialog-subtitle mt-2">{{ $t('journey.catch.escapedPrefix') }}{{ catchData.pokemonName }}{{ $t('journey.catch.escapedSuffix') }}</div>
          </template>

          <template v-else-if="catchState === 'error'">
            <div class="catch-dialog-subtitle mt-2">{{ $t('journey.catch.errorMessage') }}</div>
          </template>
        </div>

        <div class="catch-dialog-actions">
          <slot name="actions" />
        </div>
      </div>
    </ResponsiveDialog>
  </span>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import {API_ENTRYPOINT} from "@/config/entrypoint";
import PokemonDetail from "@/components/pokemon/PokemonDetail";
import ResponsiveDialog from "@/components/utils/ResponsiveDialog";

// Sjednocený catch dialog - dřív skoro identicky (~90 %) kopírovaný mezi
// FightButtons.vue (pokeball, api/catch-pokemon) a DungeonButtons.vue
// (masterball/boss, api/catch-boss-pokemon). Post-catch tlačítka (Escape/Go round
// vs. Leave dungeon/Explore more) se mezi módy liší skutečně - proto zůstávají
// na volajícím přes slot "actions", ne natvrdo v týhle komponentě.
//
// `hide-default-close` - žádná cesta ven krome akcnich tlacitek (hrac po
// neuspesnem chytani nesmi zustat zaseknuty bez Leave/Evaluate). ResponsiveDialog
// na rozdil od puvodniho `persistent` porad zavira na klik na backdrop - jen
// vlastni X krizek je schovany, ne cely "escape" mechanismus.
export default {
  name: "CatchDialog",
  components: {PokemonDetail, ResponsiveDialog},
  props: {
    fight: {type: Object, required: true},
    catchEndpoint: {type: String, required: true}, // 'api/catch-pokemon' | 'api/catch-boss-pokemon'
    ballCount: {type: Number, default: 0},
    ballIcon: {type: String, default: 'mdi-pokeball'},
    canCatch: {type: Boolean, default: false},
    // FightButtons mělo zvuk chytání, DungeonButtons ho nikdy nemělo - zachováno
    // jako prop, ne tiše sjednoceno, ať se nezmění chování boss-chytání.
    withSound: {type: Boolean, default: true},
  },
  data() {
    return {
      catchDialog: false,
      catchData: null,
      catchError: false,
      leaving: false,
      catchingLoad: false,
      catchingSound: null,
      // Zvysen pri kazdem novem pokusu i pri reset() - pozdni odpoved ze
      // starsiho pokusu se tak pozna a zahodi.
      catchToken: 0,
    }
  },
  watch: {
    catchingLoad() {
      if (this.catchingSound) {
        if (this.catchingLoad) {
          this.catchingSound.play();
        } else {
          this.catchingSound.pause();
        }
      }
    },
  },
  computed: {
    ...mapGetters({
      isLoading: 'journey/isLoading',
      user: 'user/item',
      buttonVolume: 'journey/buttonVolume',
      catchedTypesGen1: 'pokemon/catchedTypesGen1',
      catchedTypesAlola: 'pokemon/catchedTypesAlola',
    }),
    // Stavovy automat pokusu o chyceni - "Catch!" tlacitko od idle/error
    // spusti novy pokus, z pending/success/lose uz jen znovu otevre dialog
    // se stavajicim stavem (misto dalsiho requestu).
    catchState() {
      if (this.catchingLoad || this.isLoading) {
        return 'pending';
      }
      if (this.catchError) {
        return 'error';
      }
      if (this.catchData) {
        return this.catchData.success ? 'success' : 'lose';
      }
      return 'idle';
    },
    dialogHeaderTitle() {
      switch (this.catchState) {
        case 'pending':
          return this.$t('journey.catch.headerCatching');
        case 'success':
          return this.$t('journey.catch.headerSuccess');
        case 'lose':
        case 'error':
          return this.$t('journey.catch.headerFail');
        default:
          return '';
      }
    },
    opponentNumber() {
      const pokemonType = this.fight && this.fight.opponentPokemon && this.fight.opponentPokemon.pokemonType;
      return pokemonType ? pokemonType.number.toString() : null;
    },
    // Konzervativne "uz chyceno" (=> zadny glow), kdyz jeste nevime cislo
    // soupere - viz docs/superpowers/specs/2026-08-29-catch-button-glow-design.md.
    // Pozor: kontroluje se proti catchedTypesGen1/catchedTypesAlola (skutecne
    // chyceno), NE proti pokedex/pokedexAlola (jen "videno v souboji" - ty by
    // zluty glow zhasly i u druhu, kterym hrac jen porazil soupereho pokemona).
    alreadyCaught() {
      if (this.opponentNumber === null) {
        return true;
      }
      return this.catchedTypesGen1.includes(this.opponentNumber) || this.catchedTypesAlola.includes(this.opponentNumber);
    },
  },
  created() {
    this.loadPokedexNumbers();
    if (this.withSound) {
      let backgroundSound = require('@/assets/sounds/catching.mp3');
      this.catchingSound = new Audio(backgroundSound);
      this.catchingSound.volume = this.buttonVolume / 100;
      this.catchingSound.loop = true;
      this.catchingSound.addEventListener('pause', this.catchDataSound, true);
    }
  },
  methods: {
    ...mapActions({
      fetchObj: 'journey/fetchObject',
      refreshActiveTeam: 'pokemon/refreshActiveTeam',
      loadPokedexNumbers: 'pokemon/loadPokedexNumbers',
    }),
    reset() {
      this.catchToken++;
      this.catchDialog = false;
      this.catchData = null;
      this.catchError = false;
      // Bez tohohle by "pending" pri resetu uprostred bezicho pokusu zustalo
      // navzdy true - catchPokemon() dokonceni uz ho kvuli zmenenemu tokenu
      // samo nenastavi zpet na false.
      this.catchingLoad = false;
    },
    async catchPokemon() {
      if (this.catchState !== 'idle' && this.catchState !== 'error') {
        this.catchDialog = true;
        return;
      }
      this.catchData = null;
      this.catchError = false;
      // Token teto konkretni odpovedi - kdyby mezitim probehl reset() nebo
      // dalsi catchPokemon(), token uz nebude sedet a pozdni odpoved/timeout
      // se ticho zahodi misto prepsani aktualniho stavu.
      const token = ++this.catchToken;
      this.catchingLoad = true;
      this.leaving = false;
      this.catchDialog = true;
      const url = new URL(this.catchEndpoint, API_ENTRYPOINT);
      const obj = {positionId: this.user.position.id};
      const fullObj = {method: 'POST', url: url, obj: obj}
      this.fetchObj(fullObj).then(
          (data) => {
            if (token !== this.catchToken) {
              return;
            }
            if (!data) {
              // journey/fetchObject pri chybe tise vyresi promise jako
              // undefined - bez tohohle by dialog zustal navzdy v "pending".
              this.catchError = true;
              return;
            }
            this.catchData = data;
            // Spodni lista (TeamBottomSheet.vue) se o novem ulovku jinak
            // nedozvi.
            if (data.success) {
              this.refreshActiveTeam();
              // Vynuceny refresh cache "uz chyceno" - at zluty glow zmizi
              // hned, kdyby hrac narazil na stejny druh znovu ve stejne session.
              this.loadPokedexNumbers(true);
            }
          }
      );
      const delay = ms => new Promise(res => setTimeout(res, ms));
      const bonus = Math.round(10 * (Math.random(3) / 2));
      await delay(((2 + bonus) * 1000));
      if (token === this.catchToken) {
        this.catchingLoad = false;
      }
    },
    catchDataSound() {
      if (this.catchData) {
        if (this.catchData.success) {
          let backgroundSound = require('@/assets/sounds/catched.mp3');
          let sound = new Audio(backgroundSound);
          sound.volume = this.buttonVolume / 100;
          sound.play();
        } else {
          let backgroundSound = require('@/assets/sounds/escaped.mp3');
          let sound = new Audio(backgroundSound);
          sound.volume = this.buttonVolume / 100;
          sound.play();
        }
      }
    },
  },
}
</script>

<style scoped>
.catch-dialog-body {
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
}
.theme--light .catch-dialog-body {
    color: rgba(0, 0, 0, .87);
}
.theme--dark .catch-dialog-body {
    color: rgba(255, 255, 255, .87);
}
.catch-dialog-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 8px 16px;
}
.catch-dialog-subtitle {
    text-align: center;
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
}
/* leave.png ma vetsi vnitrni padding nez catching.gif, proto vetsi
   max-width/height v sablone pro vizualne stejnou velikost. */
.catch-dialog-ball {
    margin-top: 8px;
}
.catch-dialog-actions {
    flex: none;
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px 16px 16px 16px;
}
/* Jemny zlaty odlesk (sheen) prochazejici pres text i ikonu pokebalu
   spolecne (jeden spolecny span, at odlesk plyne přes obojí souvisle) -
   zvyraznuje druh, ktery hrac JESTE nema - viz
   docs/superpowers/specs/2026-08-29-catch-button-glow-design.md.
   Gradient je z vetsiny currentColor (= puvodni cerna barva textu tlacitka,
   beze zmeny), jen uzky zlaty pruh se v nem posouva - vypada to jako
   odlesk sveta na tmavem povrchu, ne jako barevne zesileni celeho textu.
   -webkit- prefixy nutne pro mobilni Chrome/Safari (PWA). */
.catch-btn-sheen {
    background: linear-gradient(100deg, currentColor 42%, #fff4c2 50%, #ffd54a 52%, currentColor 60%);
    background-size: 200% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: catch-btn-sheen-sweep 2.4s linear infinite;
}
/* Posun presne o 100% sirky vzoru (background-size) - vzhled na 0 %/100 %
   animace je pixel na pixel stejny, takze smycka nema na konci "cuk" jako
   drivejsi verze (180% -> -80%, posun 260% != background-size 250%). */
@keyframes catch-btn-sheen-sweep {
    0% { background-position: 0% 0; }
    100% { background-position: -200% 0; }
}
</style>
